import { useContext, useState, useEffect } from 'react';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Divider,
  CircularProgress
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Avatar from '@mui/material/Avatar';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import AuthContext from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import AddChoreModal from '../components/AddChoreModal';
import AddTimeoffModal from '../components/AddTimeoffModal';
import EditTimeoffModal from '../components/EditTimeoffModal';
import ChoreCommentModal from '../components/ChoreCommentModal';
import ChoresSection from '../components/ChoresSection';
import TimeoffSection from '../components/TimeoffSection';
import AddTimeoffDialog from '../components/AddTimeoffDialog';

const RELATIONSHIP_COLORS = [
    '#7B6B8D',
    '#C4856A',
    '#A7D4C4',
    '#F4C4A7',
    '#D4A7B8',
    '#C4F4B5',
    '#A7B8D4',
    '#F4D4A7',
];

const TodayPage = () => {
    const { backendUrl, authTokens } = useContext(AuthContext);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const getInitialDate = () => {
        const dateParam = searchParams.get('date');
        return dateParam ? dayjs(dateParam) : dayjs();
    };

    const [selectedDate, setSelectedDate] = useState(getInitialDate());
    const [chores, setChores] = useState([]);
    const [timeOff, setTimeOff] = useState([]);
    const [timeoffInstances, setTimeoffInstances] = useState([]);
    const [selectedTimeOff, setSelectedTimeOff] = useState({ timeoff_id: '', value: '0', hours: '0', comments: '' });
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [uuid, setUuid] = useState(null);
    const [currentRelationship, setCurrentRelationship] = useState(null);
    const [loadingChoreId, setLoadingChoreId] = useState(null); // eslint-disable-line no-unused-vars
    const initialTimeOffState = { timeoff_id: '', value: '0', hours: '0', comments: '' };
    const [relationshipName, setRelationshipName] = useState('');
    const [relationshipPicture, setRelationshipPicture] = useState('');
    const [hasActiveRelationship, setHasActiveRelationship] = useState(true);
    const [loading, setLoading] = useState(true);
    const [addChoreModalOpen, setAddChoreModalOpen] = useState(false);
    const [addTimeoffModalOpen, setAddTimeoffModalOpen] = useState(false);
    const [commentModalOpen, setCommentModalOpen] = useState(false);
    const [selectedChoreForComment, setSelectedChoreForComment] = useState(null);
    const [allRelationships, setAllRelationships] = useState([]);
    const [relationshipColorMap, setRelationshipColorMap] = useState({});
    const [editTimeoffModalOpen, setEditTimeoffModalOpen] = useState(false);
    const [selectedTimeoffForEdit, setSelectedTimeoffForEdit] = useState(null);
    const [removeCommentConfirmOpen, setRemoveCommentConfirmOpen] = useState(false);
    const [pendingCommentRemoval, setPendingCommentRemoval] = useState(null);
    const [streak, setStreak] = useState(null);
    const [reorderMode, setReorderMode] = useState(false);

    const moveChore = (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= chores.length) return;
        setChores(prev => {
            const next = [...prev];
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    const saveChoreOrder = async () => {
        const chore_ids = chores.map(c => c.chore_id);
        setReorderMode(false);
        try {
            await axiosInstance.post('api/chores/reorder/', { chore_ids });
        } catch {
            // silently fail — order is already reflected locally
        }
    };

    const getAnniversaryInfo = (dateStarted) => {
        if (!dateStarted) return null;
        const start = new Date(dateStarted + 'T00:00:00');
        const today = new Date();
        const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        let anniversary = new Date(today.getFullYear(), start.getMonth(), start.getDate());
        let years = anniversary.getFullYear() - start.getFullYear();
        if (years < 1) return null;
        const msPerDay = 24 * 60 * 60 * 1000;
        let daysUntil = Math.round((anniversary - todayMidnight) / msPerDay);
        if (daysUntil < 0) {
            anniversary = new Date(today.getFullYear() + 1, start.getMonth(), start.getDate());
            years = anniversary.getFullYear() - start.getFullYear();
            daysUntil = Math.round((anniversary - todayMidnight) / msPerDay);
        }
        if (daysUntil > 7) return null;
        return { years, daysUntil, isToday: daysUntil === 0 };
    };

    const ordinal = (n) => {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    const { myChoreWeight, partnerChoreWeight } = chores.reduce((totals, chore) => {
        const weight = chore.weight || 1;
        if (chore.chore_type === 0) {
            if (chore.value === 'me') totals.myChoreWeight += weight;
            else if (chore.value === 'partner') totals.partnerChoreWeight += weight;
        } else if (chore.chore_type === 2) {
            totals.myChoreWeight += weight * (chore.value?.me || 0);
            totals.partnerChoreWeight += weight * (chore.value?.partner || 0);
        }
        return totals;
    }, { myChoreWeight: 0, partnerChoreWeight: 0 });

    const { myTimeoffHours, partnerTimeoffHours } = timeoffInstances.reduce((totals, timeoff) => {
        const hours = timeoff.hours || 0;
        if (timeoff.value === '0') totals.myTimeoffHours += hours;
        else if (timeoff.value === '1') totals.partnerTimeoffHours += hours;
        return totals;
    }, { myTimeoffHours: 0, partnerTimeoffHours: 0 });

    useEffect(() => {
        const fetchAllRelationships = async () => {
            try {
                const response = await axiosInstance.get('api/all-relationships/');
                const relationships = response.data;
                setAllRelationships(relationships);
                const colorMap = {};
                relationships.forEach((rel, index) => {
                    colorMap[rel.relationship_id] = RELATIONSHIP_COLORS[index % RELATIONSHIP_COLORS.length];
                });
                setRelationshipColorMap(colorMap);
            } catch (error) {
                console.error('Failed to fetch relationships:', error.response?.status || error.message);
            }
        };
        fetchAllRelationships();
    }, [backendUrl, authTokens.access]);

    useEffect(() => {
        fetchRelationship(selectedDate);
    }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const dateParam = searchParams.get('date');
        if (dateParam) {
            const newDate = dayjs(dateParam);
            if (!newDate.isSame(selectedDate, 'day')) {
                setSelectedDate(newDate);
            }
        }
    }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchRelationship = async (date) => {
        const formattedDate = date.format('YYYY-MM-DD');
        setLoading(true);
        try {
            const response = await axiosInstance.get(`api/current-relationship/${formattedDate}/`);
            const data = response.data;
            setCurrentRelationship(data);
            setRelationshipName(data.partner_name);
            setRelationshipPicture(data.relationship_picture || '');
            setHasActiveRelationship(true);
            await fetchData(date);
            axiosInstance.get(`api/streak/${data.relationship_id}/`).then(r => setStreak(r.data)).catch(() => {});
        } catch (error) {
            setHasActiveRelationship(false);
            setCurrentRelationship(null);
            setRelationshipName('');
            setRelationshipPicture('');
            setStreak(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async (date) => {
        const formattedDate = date.format('YYYY-MM-DD');
        try {
            const [choresResponse, timeOffResponse, adayResponse] = await Promise.all([
                axiosInstance.get(`api/chores/?date=${formattedDate}`),
                axiosInstance.get(`api/timeoffs/?date=${formattedDate}`),
                axiosInstance.get(`api/fetch-day/?date=${formattedDate}`).catch(() => null),
            ]);

            const choresData = choresResponse.data;
            const timeOffData = timeOffResponse.data;
            setTimeOff(timeOffData);

            if (adayResponse) {
                const adayData = adayResponse.data;
                setUuid(adayData.uuid);
                setTimeoffInstances(adayData.timeoff_instances || []);
                const choresWithValues = choresData.map(chore => {
                    const choreInstance = adayData.chore_instances?.find(ci => ci.chore_id === chore.chore_id);
                    if (choreInstance) {
                        if (chore.chore_type === 0) {
                            let value = null;
                            if (choreInstance.user_value > 0) value = 'me';
                            else if (choreInstance.partner_value > 0) value = 'partner';
                            return { ...chore, value, comments: choreInstance.comments || '' };
                        } else if (chore.chore_type === 2) {
                            return { ...chore, value: { me: choreInstance.user_value, partner: choreInstance.partner_value }, comments: choreInstance.comments || '' };
                        }
                    }
                    return { ...chore, value: chore.chore_type === 2 ? { me: 0, partner: 0 } : null, comments: '' };
                });
                setChores(choresWithValues);
            } else {
                setChores(choresData.map(chore => ({
                    ...chore,
                    value: chore.chore_type === 2 ? { me: 0, partner: 0 } : null,
                    comments: ''
                })));
            }
        } catch (error) {
            // Show error but do not leave loading=true — fetchRelationship's finally handles it
            setSnackbarMessage(error.response?.data?.error || error.message || 'Failed to load data');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            // Reset data to empty so the page doesn't show stale content
            setChores([]);
            setTimeoffInstances([]);
        }
    };

    const handleDeleteTimeOffInstance = async (timeoffInstanceId) => {
        const prevTimeoffInstances = [...timeoffInstances];
        setTimeoffInstances((prev) => prev.filter((toi) => toi.timeoff_instance_id !== timeoffInstanceId));
        try {
            await axiosInstance.post('api/delete-timeoff-instance/', {
                date: selectedDate.format('YYYY-MM-DD'),
                timeoff_instance_id: timeoffInstanceId,
            });
            setSnackbarMessage('Time off deleted successfully!');
            setSnackbarSeverity('success');
        } catch (error) {
            setSnackbarMessage(error.response?.data?.error || error.message || 'Failed to delete timeoff instance');
            setSnackbarSeverity('error');
            setTimeoffInstances(prevTimeoffInstances);
        }
        setSnackbarOpen(true);
    };

    const handleResetChore = async (choreId) => {
        const prevChores = [...chores];
        setChores((prev) => prev.map((chore) =>
            chore.chore_id === choreId
                ? { ...chore, value: chore.chore_type === 2 ? { me: 0, partner: 0 } : null, comments: '' }
                : chore
        ));
        try {
            await axiosInstance.post('api/delete-chore-instance/', {
                date: selectedDate.format('YYYY-MM-DD'),
                chore_id: choreId,
                uuid,
            });
            setSnackbarMessage('Chore reset successfully!');
            setSnackbarSeverity('success');
        } catch (error) {
            setSnackbarMessage(error.response?.data?.error || error.message || 'Failed to reset chore');
            setSnackbarSeverity('error');
            setChores(prevChores);
        }
        setSnackbarOpen(true);
    };

    const handleChoreChange = async (choreId, newValue) => {
        const prevChores = [...chores];
        setChores((prev) => prev.map((chore) =>
            chore.chore_id === choreId ? { ...chore, value: newValue } : chore
        ));
        setLoadingChoreId(choreId);
        try {
            await axiosInstance.post('api/update-chore-instance/', {
                date: selectedDate.format('YYYY-MM-DD'),
                chore_id: choreId,
                value: newValue,
                uuid,
            });
        } catch (error) {
            setSnackbarMessage(error.response?.data?.error || error.message || 'Failed to update chore');
            setSnackbarSeverity('error');
            setChores(prevChores);
            setSnackbarOpen(true);
        }
        setLoadingChoreId(null);
    };

    const handleAddTimeOff = async () => {
        if (!selectedTimeOff) return;
        try {
            const response = await axiosInstance.post('api/add-timeoff-instance/', {
                date: selectedDate.format('YYYY-MM-DD'),
                timeoff_id: selectedTimeOff.timeoff_id,
                value: selectedTimeOff.value,
                comments: selectedTimeOff.comments,
                hours: selectedTimeOff.hours,
                uuid,
            });
            setTimeoffInstances((prev) => [...prev, response.data]);
            setSnackbarMessage('Time Off added successfully!');
            setSnackbarSeverity('success');
            setSelectedTimeOff(initialTimeOffState);
            setDialogOpen(false);
        } catch (error) {
            setSnackbarMessage(error.response?.data?.error || error.message || 'Failed to add Time Off');
            setSnackbarSeverity('error');
        }
        setSnackbarOpen(true);
    };

    const handleSaveComment = async (comment) => {
        if (!selectedChoreForComment) return;
        const prevChores = [...chores];
        const choreId = selectedChoreForComment.chore_id;
        setChores((prev) => prev.map((chore) =>
            chore.chore_id === choreId ? { ...chore, comments: comment } : chore
        ));
        setCommentModalOpen(false);
        setSelectedChoreForComment(null);
        try {
            await axiosInstance.post('api/update-chore-comments/', {
                date: selectedDate.format('YYYY-MM-DD'),
                chore_id: choreId,
                comments: comment,
            });
            setSnackbarMessage('Comment saved successfully!');
            setSnackbarSeverity('success');
        } catch (error) {
            setSnackbarMessage(error.response?.data?.error || error.message || 'Failed to update comments');
            setSnackbarSeverity('error');
            setChores(prevChores);
        }
        setSnackbarOpen(true);
    };

    const handleConfirmRemoveComment = async () => {
        if (!pendingCommentRemoval) return;
        const { type, id } = pendingCommentRemoval;
        setRemoveCommentConfirmOpen(false);
        setPendingCommentRemoval(null);

        if (type === 'chore') {
            const prevChores = [...chores];
            setChores(prev => prev.map(c => c.chore_id === id ? { ...c, comments: '' } : c));
            try {
                await axiosInstance.post('api/update-chore-comments/', {
                    date: selectedDate.format('YYYY-MM-DD'),
                    chore_id: id,
                    comments: '',
                });
                setSnackbarMessage('Comment removed.');
                setSnackbarSeverity('success');
            } catch (error) {
                setChores(prevChores);
                setSnackbarMessage(error.response?.data?.error || 'Failed to remove comment');
                setSnackbarSeverity('error');
            }
            setSnackbarOpen(true);
        } else if (type === 'timeoff') {
            const instance = timeoffInstances.find(t => t.timeoff_instance_id === id);
            if (!instance) return;
            const prevInstances = [...timeoffInstances];
            setTimeoffInstances(prev => prev.map(t => t.timeoff_instance_id === id ? { ...t, comments: '' } : t));
            try {
                await axiosInstance.post('api/update-timeoff-instance/', {
                    date: selectedDate.format('YYYY-MM-DD'),
                    timeoff_instance_id: id,
                    value: instance.value,
                    hours: instance.hours,
                    comments: '',
                });
                setSnackbarMessage('Comment removed.');
                setSnackbarSeverity('success');
            } catch (error) {
                setTimeoffInstances(prevInstances);
                setSnackbarMessage(error.response?.data?.error || 'Failed to remove comment');
                setSnackbarSeverity('error');
            }
            setSnackbarOpen(true);
        }
    };

    const handleSaveEditTimeoff = async (updatedData) => {
        const prevTimeoffInstances = [...timeoffInstances];
        setTimeoffInstances((prev) => prev.map((instance) =>
            instance.timeoff_instance_id === updatedData.timeoff_instance_id
                ? { ...instance, ...updatedData }
                : instance
        ));
        setEditTimeoffModalOpen(false);
        setSelectedTimeoffForEdit(null);
        try {
            await axiosInstance.post('api/update-timeoff-instance/', {
                date: selectedDate.format('YYYY-MM-DD'),
                timeoff_instance_id: updatedData.timeoff_instance_id,
                value: updatedData.value,
                hours: updatedData.hours,
                comments: updatedData.comments,
            });
            setSnackbarMessage('Time off updated successfully!');
            setSnackbarSeverity('success');
        } catch (error) {
            setSnackbarMessage(error.response?.data?.error || error.message || 'Failed to update time off');
            setSnackbarSeverity('error');
            setTimeoffInstances(prevTimeoffInstances);
        }
        setSnackbarOpen(true);
    };

    const handleChoreAdded = (newChore) => {
        setChores(prev => [...prev, { ...newChore, value: null }]);
        setSnackbarMessage('Chore added successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    const handleAddStarterChores = async (names) => {
        try {
            const response = await axiosInstance.post('api/chores/bulk/', {
                names,
                relationship_id: currentRelationship?.relationship_id,
            });
            const added = response.data.map(c => ({ ...c, value: null }));
            setChores(prev => [...prev, ...added]);
            setSnackbarMessage(`Added ${added.length} starter chore${added.length !== 1 ? 's' : ''}!`);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            const msg = error.response?.data?.error || 'Failed to add starter chores.';
            setSnackbarMessage(msg);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            throw error;
        }
    };

    const handleTimeoffAdded = (newTimeoff) => {
        setTimeOff(prev => [...prev, newTimeoff]);
        setSelectedTimeOff({ ...selectedTimeOff, timeoff_id: newTimeoff.timeoff_id });
        setSnackbarMessage('Time off added successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    const getRelationshipForDate = (date) => {
        const dateStr = date.format('YYYY-MM-DD');
        for (const rel of allRelationships) {
            const startDate = rel.date_started;
            const endDate = rel.date_ended || '9999-12-31';
            if (dateStr >= startDate && dateStr <= endDate) return rel.relationship_id;
        }
        return null;
    };

    const CustomDay = (props) => {
        const { day, outsideCurrentMonth, ...other } = props;
        const relationshipId = getRelationshipForDate(day);
        const color = relationshipId ? relationshipColorMap[relationshipId] : null;
        return (
            <PickersDay
                {...other}
                day={day}
                outsideCurrentMonth={outsideCurrentMonth}
                sx={{
                    ...(color && {
                        backgroundColor: `${color}20`,
                        border: `2px solid ${color}`,
                        fontWeight: 'bold',
                        '&:hover': { backgroundColor: `${color}40` },
                        '&.Mui-selected': { backgroundColor: color, '&:hover': { backgroundColor: color } },
                    }),
                }}
            />
        );
    };

    const isToday = (date) => date.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');

    return (
        <>
        <SEO title="Today - Couplendar" />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ minHeight: '100vh', background: '#F9F7F4', py: { xs: 3, md: 4 } }}>
                <Container maxWidth="lg">
                    {/* Date Picker and Relationship Name */}
                    <Card sx={{ mb: 3, background: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', borderRadius: 3, border: 'none' }}>
                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <IconButton
                                        onClick={() => setSelectedDate(selectedDate.subtract(1, 'day'))}
                                        sx={{ color: '#7B6B8D', '&:hover': { backgroundColor: 'rgba(184, 167, 212, 0.1)' } }}
                                    >
                                        <ChevronLeftIcon />
                                    </IconButton>
                                    <DatePicker
                                        label="Select Date"
                                        value={selectedDate}
                                        onChange={(date) => setSelectedDate(date)}
                                        slots={{ day: CustomDay }}
                                        slotProps={{
                                            textField: {
                                                sx: {
                                                    '& .MuiOutlinedInput-root': { borderRadius: 2, '&.Mui-focused fieldset': { borderColor: '#7B6B8D' } },
                                                    '& .MuiInputLabel-root.Mui-focused': { color: '#7B6B8D' }
                                                }
                                            }
                                        }}
                                    />
                                    <IconButton
                                        onClick={() => setSelectedDate(selectedDate.add(1, 'day'))}
                                        sx={{ color: '#7B6B8D', '&:hover': { backgroundColor: 'rgba(184, 167, 212, 0.1)' } }}
                                    >
                                        <ChevronRightIcon />
                                    </IconButton>
                                    {!isToday(selectedDate) && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setSelectedDate(dayjs())}
                                            sx={{
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: '0.8rem',
                                                borderColor: '#7B6B8D',
                                                color: '#7B6B8D',
                                                px: 1.5,
                                                py: 0.5,
                                                minWidth: 0,
                                                '&:hover': { borderColor: '#6A5A7A', backgroundColor: 'rgba(123,107,141,0.06)' },
                                            }}
                                        >
                                            Today
                                        </Button>
                                    )}
                                    <Typography variant="h6" sx={{ color: '#7B6B8D', fontWeight: 600, ml: 1 }}>
                                        {selectedDate.format('dddd')}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    {streak && streak.streak >= 2 && (
                                        <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, backgroundColor: 'rgba(255,152,0,0.1)', border: '1px solid #FF9800' }}>
                                            <Typography variant="caption" sx={{ color: '#E65100', fontWeight: 600 }}>
                                                🔥 {streak.streak}-day streak
                                            </Typography>
                                        </Box>
                                    )}
                                    {streak && streak.streak < 2 && (
                                        <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, backgroundColor: 'rgba(123,107,141,0.08)', border: '1px solid #C0B0D0' }}>
                                            <Typography variant="caption" sx={{ color: '#7B6B8D', fontWeight: 500 }}>
                                                📅 {streak.this_month}/{streak.days_in_month} days this month
                                            </Typography>
                                        </Box>
                                    )}
                                    {relationshipName && (
                                        <Stack
                                            direction="row" alignItems="center" spacing={1.5}
                                            onClick={() => navigate('/relationship')}
                                            sx={{ cursor: 'pointer', transition: 'opacity 0.2s ease', '&:hover': { opacity: 0.7 } }}
                                        >
                                            {relationshipPicture && (
                                                <Avatar
                                                    src={`${backendUrl}${relationshipPicture}`}
                                                    alt={relationshipName}
                                                    sx={{ width: 40, height: 40, border: '2px solid #7B6B8D' }}
                                                />
                                            )}
                                            <Typography variant="h6" sx={{ color: '#7B6B8D', fontWeight: 600 }}>
                                                {relationshipName}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* Anniversary Banner */}
                    {(() => {
                        const info = getAnniversaryInfo(currentRelationship?.date_started);
                        if (!info || currentRelationship?.date_ended) return null;
                        const title = info.isToday
                            ? `Happy ${ordinal(info.years)} Anniversary! 🎉`
                            : `${ordinal(info.years)} Anniversary in ${info.daysUntil} day${info.daysUntil !== 1 ? 's' : ''} 💕`;
                        return (
                            <Card sx={{ mb: 3, background: 'linear-gradient(135deg, rgba(196,133,106,0.08) 0%, rgba(123,107,141,0.08) 100%)', border: '1px solid rgba(196,133,106,0.4)', boxShadow: 'none', borderRadius: 3 }}>
                                <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        {relationshipPicture ? (
                                            <Avatar
                                                src={`${backendUrl}${relationshipPicture}`}
                                                alt={relationshipName}
                                                sx={{ width: 52, height: 52, border: '2px solid #C4856A' }}
                                            />
                                        ) : (
                                            <Typography sx={{ fontSize: '2rem', lineHeight: 1 }}>💑</Typography>
                                        )}
                                        <Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#C4856A', letterSpacing: 0.2 }}>
                                                {title}
                                            </Typography>
                                            {!info.isToday && (
                                                <Typography variant="caption" sx={{ color: '#7B6B8D', fontWeight: 500 }}>
                                                    {info.years} year{info.years !== 1 ? 's' : ''} together
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        );
                    })()}

                    {/* Loading Spinner */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', borderRadius: 3 }}>
                            <CircularProgress size={60} sx={{ color: '#7B6B8D' }} />
                        </Box>
                    ) : (
                        <>
                        {!hasActiveRelationship ? (
                            <Card sx={{ background: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', borderRadius: 3, border: 'none' }}>
                                <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                                    <Typography variant="h5" gutterBottom color="text.secondary">
                                        No Active Relationship
                                    </Typography>
                                    <Typography variant="body1" sx={{ marginBottom: 2 }} color="text.secondary">
                                        There is no active relationship for this date.
                                    </Typography>
                                    {isToday(selectedDate) && (
                                        <>
                                            <Typography variant="body2" sx={{ marginBottom: 2 }} color="text.secondary">
                                                Would you like to start a new relationship?
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                onClick={() => navigate('/relationship')}
                                                sx={{ borderRadius: 2, py: 1.2, background: '#7B6B8D', boxShadow: 'none', '&:hover': { background: '#6A5A7A', boxShadow: 'none' } }}
                                            >
                                                Go to Relationship Page
                                            </Button>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        ) : currentRelationship?.date_ended ? (
                            <Card sx={{ background: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', borderRadius: 3, border: 'none' }}>
                                <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                                    <Typography variant="h5" gutterBottom color="text.secondary">
                                        Relationship Ended
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        This relationship ended on {new Date(currentRelationship.date_ended).toLocaleDateString()}. Day entries cannot be viewed or edited.
                                    </Typography>
                                </CardContent>
                            </Card>
                        ) : (
                            <Card sx={{ background: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', borderRadius: 3, border: 'none' }}>
                                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} divider={<Divider orientation="vertical" flexItem />}>
                                        <ChoresSection
                                            chores={chores}
                                            myChoreWeight={myChoreWeight}
                                            partnerChoreWeight={partnerChoreWeight}
                                            relationshipName={relationshipName}
                                            onAddChore={() => setAddChoreModalOpen(true)}
                                            onChoreChange={handleChoreChange}
                                            onResetChore={handleResetChore}
                                            onOpenComment={(chore) => { setSelectedChoreForComment(chore); setCommentModalOpen(true); }}
                                            onRemoveComment={(type, id) => { setPendingCommentRemoval({ type, id }); setRemoveCommentConfirmOpen(true); }}
                                            onAddStarterChores={handleAddStarterChores}
                                            reorderMode={reorderMode}
                                            onToggleReorder={() => setReorderMode(true)}
                                            onMoveChore={moveChore}
                                            onSaveOrder={saveChoreOrder}
                                        />
                                        <TimeoffSection
                                            timeoffInstances={timeoffInstances}
                                            myTimeoffHours={myTimeoffHours}
                                            partnerTimeoffHours={partnerTimeoffHours}
                                            relationshipName={relationshipName}
                                            onAddClick={() => { setSelectedTimeOff(initialTimeOffState); setDialogOpen(true); }}
                                            onEdit={(instance) => { setSelectedTimeoffForEdit(instance); setEditTimeoffModalOpen(true); }}
                                            onDelete={handleDeleteTimeOffInstance}
                                            onRemoveComment={(type, id) => { setPendingCommentRemoval({ type, id }); setRemoveCommentConfirmOpen(true); }}
                                        />
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}
                        </>
                    )}

                    {/* Add Time Off Dialog */}
                    {hasActiveRelationship && !currentRelationship?.date_ended && (
                        <AddTimeoffDialog
                            open={dialogOpen}
                            onClose={() => setDialogOpen(false)}
                            onAdd={handleAddTimeOff}
                            timeOff={timeOff}
                            selectedTimeOff={selectedTimeOff}
                            onSelectedTimeOffChange={setSelectedTimeOff}
                            onOpenAddTimeoffModal={() => setAddTimeoffModalOpen(true)}
                            relationshipName={relationshipName}
                        />
                    )}

                    <AddChoreModal
                        open={addChoreModalOpen}
                        onClose={() => setAddChoreModalOpen(false)}
                        onChoreAdded={handleChoreAdded}
                        backendUrl={backendUrl}
                        authTokens={authTokens}
                        currentRelationship={currentRelationship}
                    />

                    <AddTimeoffModal
                        open={addTimeoffModalOpen}
                        onClose={() => setAddTimeoffModalOpen(false)}
                        onTimeoffAdded={handleTimeoffAdded}
                        backendUrl={backendUrl}
                        authTokens={authTokens}
                        currentRelationship={currentRelationship}
                    />

                    {/* Remove Comment Confirmation Dialog */}
                    <Dialog
                        open={removeCommentConfirmOpen}
                        onClose={() => setRemoveCommentConfirmOpen(false)}
                        maxWidth="xs"
                        fullWidth
                        PaperProps={{ sx: { borderRadius: 3 } }}
                    >
                        <DialogTitle>Remove Comment</DialogTitle>
                        <DialogContent>
                            <Typography>Are you sure you want to remove this comment?</Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setRemoveCommentConfirmOpen(false)} color="inherit">Cancel</Button>
                            <Button onClick={handleConfirmRemoveComment} color="error" variant="contained" disableElevation>Remove</Button>
                        </DialogActions>
                    </Dialog>

                    <ChoreCommentModal
                        open={commentModalOpen}
                        onClose={() => { setCommentModalOpen(false); setSelectedChoreForComment(null); }}
                        onSave={handleSaveComment}
                        chore={selectedChoreForComment}
                    />

                    <EditTimeoffModal
                        open={editTimeoffModalOpen}
                        onClose={() => { setEditTimeoffModalOpen(false); setSelectedTimeoffForEdit(null); }}
                        onSave={handleSaveEditTimeoff}
                        timeoffInstance={selectedTimeoffForEdit}
                        relationshipName={relationshipName}
                    />

                    <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
                        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                </Container>
            </Box>
        </LocalizationProvider>
        </>
    );
};

export default TodayPage;
